import React, { useEffect, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { PlaceOutlined as PlaceIcon } from '@mui/icons-material';

import { addressDetails, autocompleteAddress } from '../../actions/cartActions';
import {
    EMPTY_ADDRESS,
    PROVINCES,
    newSessionToken,
    provinceNameFor,
} from '../../lib/address';
import { COLORS } from '../storefront/storefrontStyles';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 3;
const NO_SUGGESTIONS = [];

// Campos con la estética oscura del checkout.
export const darkFieldSx = {
    '& .MuiInputBase-root': {
        color: COLORS.text,
        backgroundColor: 'rgba(255,255,255,0.04)',
    },
    '& .MuiInputLabel-root': { color: COLORS.muted },
    '& .MuiInputLabel-root.Mui-focused': { color: COLORS.call },
    '& .MuiInputLabel-root.Mui-error': { color: '#f28b82' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.borderHover,
    },
    '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.call,
    },
    '& .MuiInputBase-root.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: '#f28b82',
    },
    '& .MuiInputBase-root.Mui-disabled': {
        color: COLORS.subtle,
        WebkitTextFillColor: COLORS.subtle,
    },
    '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: COLORS.subtle,
    },
    '& .MuiFormHelperText-root': { color: COLORS.subtle },
    '& .MuiFormHelperText-root.Mui-error': { color: '#f28b82' },
    '& .MuiSvgIcon-root': { color: COLORS.muted },
};

const popupPaperSx = {
    backgroundColor: COLORS.card,
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
    '& .MuiAutocomplete-option.Mui-focused': {
        backgroundColor: 'rgba(255,255,255,0.08)',
    },
    '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
        color: COLORS.muted,
    },
};

const selectMenuSx = {
    '& .MuiPaper-root': {
        backgroundColor: COLORS.card,
        color: COLORS.text,
        maxHeight: 320,
    },
};

// Dirección de entrega: búsqueda con Google Places (vía la API) y los campos
// que Correo necesita. El número se pide a mano si Google no lo trae; piso y
// depto son opcionales. Controlado: el checkout tiene la dirección y cotiza
// cuando cambia el código postal.
const HomeDeliveryForm = ({ address, onChange, disabled }) => {
    const [input, setInput] = useState('');
    const [suggestions, setSuggestions] = useState(NO_SUGGESTIONS);
    const [searching, setSearching] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState(null);
    // los campos aparecen después de elegir una sugerencia (o "a mano")
    const [showFields, setShowFields] = useState(
        Boolean(address && address.streetName)
    );
    const [numberMissing, setNumberMissing] = useState(false);

    const sessionToken = useRef(newSessionToken());
    const searchSeq = useRef(0);
    const numberRef = useRef(null);

    // Búsqueda con debounce; una respuesta vieja nunca pisa a una nueva.
    useEffect(() => {
        const text = input.trim();
        if (text.length < SEARCH_MIN_CHARS) {
            searchSeq.current += 1;
            setSuggestions(NO_SUGGESTIONS);
            setSearching(false);
            return undefined;
        }

        const seq = ++searchSeq.current;
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const result = await autocompleteAddress(
                    text,
                    sessionToken.current
                );
                if (seq !== searchSeq.current) return;
                setSuggestions(result);
                setError(null);
            } catch (err) {
                if (seq !== searchSeq.current) return;
                setSuggestions(NO_SUGGESTIONS);
                setError(err.message);
            } finally {
                if (seq === searchSeq.current) setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [input]);

    const set = (field) => (e) =>
        onChange({ ...address, [field]: e.target.value });

    const handleSelect = async (suggestion) => {
        if (!suggestion) return;
        // cierra la búsqueda: lo que se tipee después es una nueva
        searchSeq.current += 1;
        setSearching(false);
        setSuggestions(NO_SUGGESTIONS);
        setLoadingDetails(true);
        setError(null);

        try {
            const details = await addressDetails(
                suggestion.placeId,
                sessionToken.current
            );
            const streetNumber = (details.streetNumber || '').trim();
            onChange({
                ...EMPTY_ADDRESS,
                // piso/depto se conservan si ya estaban cargados
                floor: address.floor,
                apartment: address.apartment,
                streetName: details.streetName || '',
                streetNumber,
                city: details.locality || '',
                province: provinceNameFor(details.provinceName),
                postalCode: details.postalCode || '',
            });
            setShowFields(true);
            setNumberMissing(!streetNumber);
            if (!streetNumber) {
                setTimeout(
                    () => numberRef.current && numberRef.current.focus(),
                    0
                );
            }
        } catch (err) {
            setError(err.message);
        } finally {
            sessionToken.current = newSessionToken();
            setLoadingDetails(false);
        }
    };

    const numberError = numberMissing && !address.streetNumber.trim();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Autocomplete
                options={suggestions}
                filterOptions={(options) => options}
                getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.label
                }
                isOptionEqualToValue={(a, b) => a.placeId === b.placeId}
                value={null}
                inputValue={input}
                onInputChange={(e, value, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                        setInput(value);
                    }
                }}
                onChange={(e, option) => {
                    if (option && typeof option !== 'string') {
                        setInput(option.label);
                        handleSelect(option);
                    }
                }}
                loading={searching}
                loadingText="Buscando…"
                noOptionsText={
                    input.trim().length < SEARCH_MIN_CHARS
                        ? 'Escribí la calle y el número'
                        : 'No encontramos esa dirección'
                }
                disabled={disabled || loadingDetails}
                slotProps={{ paper: { sx: popupPaperSx } }}
                renderOption={(props, option) => {
                    // eslint-disable-next-line no-unused-vars
                    const { key, ...rest } = props;
                    return (
                        <Box
                            component="li"
                            key={option.placeId}
                            {...rest}
                            sx={{ display: 'flex', gap: 1 }}
                        >
                            <PlaceIcon
                                sx={{ color: COLORS.muted, fontSize: 20 }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.95rem' }}>
                                    {option.mainText || option.label}
                                </Typography>
                                {option.secondaryText && (
                                    <Typography
                                        sx={{
                                            color: COLORS.muted,
                                            fontSize: '0.8rem',
                                        }}
                                    >
                                        {option.secondaryText}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Dirección de entrega"
                        placeholder="Ej.: Córdoba 1200, Rosario"
                        error={Boolean(error)}
                        helperText={
                            error ||
                            (loadingDetails
                                ? 'Cargando la dirección…'
                                : 'Buscá la calle y el número.')
                        }
                        sx={darkFieldSx}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {(searching || loadingDetails) && (
                                            <CircularProgress
                                                size={18}
                                                sx={{ color: COLORS.muted }}
                                            />
                                        )}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            },
                        }}
                    />
                )}
            />

            {!showFields && (
                <Button
                    onClick={() => setShowFields(true)}
                    disabled={disabled}
                    sx={{
                        alignSelf: 'flex-start',
                        color: COLORS.muted,
                        textTransform: 'none',
                        px: 0,
                        '&:hover': { color: 'white' },
                    }}
                >
                    ¿No la encontrás? Cargala a mano
                </Button>
            )}

            {showFields && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr 1fr',
                            sm: '2fr 1fr 1fr 1fr',
                        },
                        gap: 1.5,
                    }}
                >
                    <TextField
                        label="Calle"
                        value={address.streetName}
                        onChange={set('streetName')}
                        disabled={disabled}
                        required
                        sx={{
                            ...darkFieldSx,
                            gridColumn: { xs: '1 / -1', sm: 'auto' },
                        }}
                    />
                    <TextField
                        label="Número"
                        value={address.streetNumber}
                        onChange={set('streetNumber')}
                        inputRef={numberRef}
                        disabled={disabled}
                        required
                        error={numberError}
                        helperText={numberError ? 'Ingresá la altura.' : ''}
                        slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                        sx={darkFieldSx}
                    />
                    <TextField
                        label="Piso"
                        value={address.floor}
                        onChange={set('floor')}
                        disabled={disabled}
                        sx={darkFieldSx}
                    />
                    <TextField
                        label="Depto"
                        value={address.apartment}
                        onChange={set('apartment')}
                        disabled={disabled}
                        sx={darkFieldSx}
                    />
                    <TextField
                        label="Ciudad"
                        value={address.city}
                        onChange={set('city')}
                        disabled={disabled}
                        required
                        sx={{
                            ...darkFieldSx,
                            gridColumn: { xs: '1 / -1', sm: 'span 2' },
                        }}
                    />
                    <TextField
                        select
                        label="Provincia"
                        value={address.province}
                        onChange={set('province')}
                        disabled={disabled}
                        required
                        slotProps={{
                            select: { MenuProps: { sx: selectMenuSx } },
                        }}
                        sx={darkFieldSx}
                    >
                        {PROVINCES.map((p) => (
                            <MenuItem key={p.name} value={p.name}>
                                {p.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Código postal"
                        value={address.postalCode}
                        onChange={set('postalCode')}
                        disabled={disabled}
                        required
                        slotProps={{ htmlInput: { maxLength: 8 } }}
                        sx={darkFieldSx}
                    />
                </Box>
            )}
        </Box>
    );
};

export default HomeDeliveryForm;
